package com.steamstreet.vegasful.web

import kotlinx.html.FlowContent
import kotlinx.html.span

/**
 * Class that runs some aspect of a client interaction
 */
interface ClientController {
    /**
     * Start this controller.
     */
    suspend fun run()
}

const val CONTROLLER_ATTRIBUTE = "data-account-module"

sealed class ClientControllerDefinition(val id: String) {
    fun install(content: FlowContent) {
        content.attributes[CONTROLLER_ATTRIBUTE] = id
    }
}

/**
 * Controller definition responsible for recording engagement with the entity whose page is being
 * shown: the visit itself, and any subsequent sign that the visitor went deeper than a glance.
 * Install this on entity detail pages only — a listing page is not a view of any one entity.
 */
object EntityViewedController : ClientControllerDefinition("entity-viewed") {
    const val pathAttribute = "data-entity-path"
    fun install(content: FlowContent, entityPath: String) {
        install(content)
        content.attributes[pathAttribute] = entityPath
    }
}

/**
 * Marks a region containing a link to buy tickets. A click inside it is recorded as a TICKETS
 * interaction against the entity path held in the attribute.
 */
const val TICKETS_LINK_ATTRIBUTE = "data-tickets-path"

/**
 * Marks a region containing a link that takes the visitor deeper into an entity's details, such as
 * the event's own site. A click inside it is recorded as a MORE_INFO interaction.
 */
const val MORE_INFO_LINK_ATTRIBUTE = "data-more-info-path"

/**
 * Wrap content so that clicking it records an interaction against the given entity. The wrapper is
 * removed from layout, so it can be placed around a button or link without disturbing the design.
 * A null entity path renders the content unwrapped, for call sites where the link is only sometimes
 * worth recording.
 */
private fun FlowContent.interactionWrapper(attribute: String, entityPath: String?, content: FlowContent.() -> Unit) {
    if (entityPath == null) {
        content()
        return
    }
    span {
        attributes[attribute] = entityPath
        attributes["style"] = "display: contents"
        content()
    }
}

/**
 * Wrap a ticket link so that clicking it is recorded as a TICKETS interaction.
 */
fun FlowContent.ticketsLink(entityPath: String?, content: FlowContent.() -> Unit) {
    interactionWrapper(TICKETS_LINK_ATTRIBUTE, entityPath, content)
}

/**
 * Wrap a link to further detail so that clicking it is recorded as a MORE_INFO interaction.
 */
fun FlowContent.moreInfoLink(entityPath: String?, content: FlowContent.() -> Unit) {
    interactionWrapper(MORE_INFO_LINK_ATTRIBUTE, entityPath, content)
}